"use client";

import { useEffect, useRef, useState } from "react";
import { Check, ShoppingBag, X } from "lucide-react";
import { useCommunityCart } from "./community-cart-provider";

interface AddToCartButtonProps {
  shopSlug: string;
  variantId: string;
  quantity?: number;
  disabled?: boolean;
  label?: string;
  className?: string;
  variant?: "primary" | "icon";
}

export function AddToCartButton({
  shopSlug,
  variantId,
  quantity = 1,
  disabled = false,
  label = "Add to cart",
  className = "",
  variant = "primary",
}: AddToCartButtonProps) {
  const { addItem } = useCommunityCart();
  const [state, setState] = useState<"idle" | "loading" | "added" | "error">(
    "idle",
  );
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const handleClick = async () => {
    if (disabled || state === "loading") return;
    setState("loading");
    try {
      await addItem({ shop_slug: shopSlug, variant_id: variantId, quantity });
      setState("added");
      timer.current = setTimeout(() => setState("idle"), 1500);
    } catch {
      setState("error");
      timer.current = setTimeout(() => setState("idle"), 2500);
    }
  };

  const isDisabled = disabled || state === "loading";

  if (variant === "icon") {
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={isDisabled}
        title={label}
        aria-label={
          state === "added"
            ? "Added to cart"
            : state === "error"
              ? "Add to cart failed — try again"
              : label
        }
        aria-live="polite"
        className={`inline-flex h-11 w-11 items-center justify-center rounded-full shadow-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 ${
          isDisabled
            ? "bg-raised text-muted cursor-not-allowed"
            : state === "error"
              ? "bg-panel text-danger hover:bg-danger-soft active:bg-danger-soft"
              : "bg-panel text-brand-green hover:bg-brand-green-soft active:bg-brand-green-soft"
        } ${className}`}
      >
        {state === "loading" ? (
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-brand-green-line border-t-primary-600" />
        ) : state === "added" ? (
          <Check className="h-5 w-5" aria-hidden="true" />
        ) : state === "error" ? (
          <X className="h-5 w-5" aria-hidden="true" />
        ) : (
          <ShoppingBag className="h-5 w-5" aria-hidden="true" />
        )}
        <span className="sr-only" role="status">
          {state === "added"
            ? "Added to cart"
            : state === "loading"
              ? "Adding to cart"
              : state === "error"
                ? "Add to cart failed"
                : ""}
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isDisabled}
      aria-live="polite"
      className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-none border-2 px-5 py-2.5 text-sm font-mono font-black uppercase tracking-[0.12em] transition-[translate,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 ${
        isDisabled
          ? disabled
            ? "border-line-strong bg-raised text-muted cursor-not-allowed shadow-none"
            : "border-retro-line bg-retro-paper text-brand-green/60 cursor-wait shadow-[4px_4px_0_#173c17]"
          : state === "error"
            ? "border-red-900 bg-red-600 text-white shadow-[4px_4px_0_#7f1d1d] motion-safe:hover:translate-x-0.5 motion-safe:hover:translate-y-0.5 hover:shadow-[2px_2px_0_#7f1d1d] motion-safe:active:translate-x-1 motion-safe:active:translate-y-1 active:shadow-none"
            : "border-retro-line bg-retro-paper text-brand-green shadow-[4px_4px_0_#173c17] motion-safe:hover:translate-x-0.5 motion-safe:hover:translate-y-0.5 hover:shadow-[2px_2px_0_#173c17] motion-safe:active:translate-x-1 motion-safe:active:translate-y-1 active:shadow-none"
      } ${className}`}
    >
      {state === "loading" && (
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-retro-line/30 border-t-primary-900" />
      )}
      {state === "added"
        ? "Added ✓"
        : state === "loading"
          ? "Adding…"
          : state === "error"
            ? "Failed — try again"
            : label}
    </button>
  );
}
